import { useNavigate } from 'react-router-dom'
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileText,
  RefreshCw,
  Shield,
  Target,
} from 'lucide-react'
import { useI18n } from '../../../i18n'
import { Button } from '../../ui/Button'
import { formatRelativeTime } from '../../../utils/operationalUx'
import type { RuntimeTimelineEvent, TimelineEventType } from '../../../services/executivePostureApi'
import entStyles from '../../../styles/enterprise/EnterpriseOperationalUx.module.scss'
import widgetStyles from '../../../styles/pages/home/ExecutivePostureWidgets.module.scss'

type Props = {
  events: RuntimeTimelineEvent[]
  loading?: boolean
  recomputing?: boolean
  onRecompute?: () => void
}

function EventIcon({ type }: { type: TimelineEventType }) {
  const className = widgetStyles.timelineIcon
  switch (type) {
    case 'EVIDENCE':
      return <FileText size={16} className={className} aria-hidden />
    case 'REMEDIATION':
      return <RefreshCw size={16} className={className} aria-hidden />
    case 'AUDIT':
      return <CheckCircle2 size={16} className={className} aria-hidden />
    case 'SCOPE':
      return <Target size={16} className={className} aria-hidden />
    case 'READINESS':
      return <Shield size={16} className={className} aria-hidden />
    default:
      return <Clock size={16} className={className} aria-hidden />
  }
}

function severityClass(severity?: string): string {
  const s = String(severity || 'INFO').toUpperCase()
  if (s === 'CRITICAL') return widgetStyles.timelineSeverityCritical
  if (s === 'WARNING') return widgetStyles.timelineSeverityWarning
  return widgetStyles.timelineSeverityInfo
}

export default function RuntimeGovernanceTimeline({
  events,
  loading,
  recomputing,
  onRecompute,
}: Props) {
  const { t } = useI18n()
  const navigate = useNavigate()

  return (
    <div className={entStyles.posturePanel} data-testid="runtime-governance-timeline">
      <h2 className={entStyles.postureTitle}>
        {t('home.executivePosture.timeline.title', 'Runtime Governance Timeline')}
      </h2>
      <p className={entStyles.postureSubtitle}>
        {t(
          'home.executivePosture.timeline.subtitle',
          'Operational compliance, evidence, audit, and governance activity.',
        )}
      </p>

      {loading ? (
        <p className={entStyles.emptyState}>{t('common.loading', 'Loading…')}</p>
      ) : !events.length ? (
        <div className={widgetStyles.timelineEmptyPanel}>
          <p className={widgetStyles.timelineEmptyTitle}>
            {t('home.executivePosture.timeline.empty', 'No recent governance activity')}
          </p>
          <p className={widgetStyles.timelineEmptyDescription}>
            {t(
              'home.executivePosture.timeline.emptyDescription',
              'Workflow, evidence, remediation, audit, and policy events will appear here as your compliance program changes.',
            )}
          </p>
          <div className={widgetStyles.timelineEmptyActions}>
            <Button
              type="button"
              size="small"
              color="outline"
              onClick={() => navigate('/compliance-center/operational-events')}
            >
              {t('home.executivePosture.timeline.openEvents', 'Open Operational events')}
            </Button>
            {onRecompute ? (
              <Button
                type="button"
                size="small"
                color="primary"
                className="!bg-green-600 hover:!bg-green-700 !border-green-600"
                disabled={recomputing}
                onClick={() => onRecompute()}
              >
                {recomputing
                  ? t('common.refreshing', 'Refreshing…')
                  : t('home.executivePosture.timeline.recompute', 'Recompute governance posture')}
              </Button>
            ) : null}
          </div>
        </div>
      ) : (
        <ul className={widgetStyles.timelineList}>
          {events.map((ev) => (
            <li key={ev.id} className={`${widgetStyles.timelineItem} ${severityClass(ev.severity)}`}>
              <div className={widgetStyles.timelineIconWrap}>
                <EventIcon type={ev.type} />
                {ev.severity === 'CRITICAL' || ev.severity === 'WARNING' ? (
                  <AlertTriangle size={12} className={widgetStyles.timelineSeverityDot} aria-hidden />
                ) : null}
              </div>
              <div className={widgetStyles.timelineContent}>
                <p className={widgetStyles.timelineTitle}>{ev.title}</p>
                <p className={widgetStyles.timelineMeta}>
                  <span>{formatRelativeTime(ev.createdAt)}</span>
                  {ev.systemName ? <span> · {ev.systemName}</span> : null}
                  {ev.standardCode ? <span> · {ev.standardCode}</span> : null}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
